// The tool runner: how an online tool (a resource item of kind 'tool', 1 HTML file in private
// storage) runs inside the hub (decision 8; the map's critic, build_now 10). Pure: no network, no
// React and no imports, so a test and scripts/build.mjs can import it as it is.
//
// Why a runner. Supabase Storage answers an HTML file as plain text, so a signed link opened in a
// tab shows the tool's source. And the hub's own Content-Security-Policy lets no inline script run,
// which every tool is made of. So the tool page (ToolPage.jsx, /<product>/tools/<placement id>)
// fetches the file through sign-file's 2-minute link and hands it to a page of its own:
//
// - /tools/run.html (runnerPage below, written into dist/ by scripts/build.mjs), served with its own
//   Content-Security-Policy (TOOL_FRAME_CSP): inline script and style, the fonts and images it's
//   given as data, and nothing else. No network at all: no fetch, no script, stylesheet, font or
//   image from any address, no form posting anywhere, no frame inside it. Only the hub may frame it.
// - It's framed with TOOL_SANDBOX: allow-scripts, never allow-same-origin, so the tool runs with an
//   origin of its own (null). It can't read the hub's storage (where the login is), its cookies or
//   the page around it. allow-popups and allow-popups-to-escape-sandbox let a link the tool opens
//   in a new tab (the vendor finance analyser's HMRC calculator) work as an ordinary tab, and
//   allow-modals lets its print button print. The frame may write to the clipboard (TOOL_ALLOW), as
//   the analysers' Copy buttons do.
// - The runner page asks for the tool when it's ready ({ hubTool: 'ready' }), and the hub posts it
//   ({ hubTool: 'run', html }); the runner writes it in place of itself, under the same policy. The
//   hub answers only the frame it made, and only once a frame: a frame the tool has navigated to
//   another page is the same window, so a second 'ready' gets nothing. The tool's own messages carry
//   a channel, a random value the hub wrote into this copy of the tool, and a message without it
//   (from a page the frame has navigated to) is refused, so no other page is ever handed the tool or
//   the member's working, or can write their working.
// - The member's working kept in the hub's own storage (savedKey) is cleared when they log out, on
//   every way out (lib/auth.js), so the next person on a shared computer can't read it.
//
// What prepareToolHtml changes in a tool before it runs:
// - a link or @import to Google Fonts (fonts.googleapis.com, fonts.gstatic.com) is taken out, and
//   the hub's own copies of the families the tool names (Archivo, IBM Plex Sans, Inter, Inter Tight,
//   Manrope, Anton) go in as data, so nothing loads from a public CDN (CLAUDE.md, Stack);
// - localStorage and sessionStorage, which a frame with no origin can't have, become stand-ins in
//   memory. localStorage's is kept for this member in the hub's own storage (ToolPage.jsx), so a tool
//   that remembers its working (the assisted sale analyser) still does, in this browser;
// - the tool reports its height ({ hubTool: 'height', height }), so the page grows with it and the
//   tool scrolls with the page, as the live course site sizes its analysers.
// Anything else the tool asks for from another address is refused by the policy. A tool that needs
// a script from a CDN (React, Babel) doesn't run here: it stays a link to where it lives.

export const TOOL_FRAME_PATH = '/tools/run.html'
export const TOOL_SANDBOX = 'allow-scripts allow-popups allow-popups-to-escape-sandbox allow-modals'
export const TOOL_ALLOW = 'clipboard-write'

// The runner's policy. The header carries every directive; the copy in the page itself (for the
// local dev server, which sends no headers) leaves out frame-ancestors, which a <meta> can't carry.
const DIRECTIVES = [
  "default-src 'none'",
  "script-src 'unsafe-inline'",
  "style-src 'unsafe-inline'",
  'font-src data:',
  'img-src data: blob:',
  'media-src data: blob:',
  "connect-src 'none'",
  "frame-src 'none'",
  "worker-src 'none'",
  "manifest-src 'none'",
  "object-src 'none'",
  "base-uri 'none'",
  "form-action 'none'",
]
export const TOOL_META_CSP = DIRECTIVES.join('; ')
export const TOOL_FRAME_CSP = [...DIRECTIVES, "frame-ancestors 'self'"].join('; ')

// The most a tool's file can be, and the most of its working kept for a member, in characters
export const TOOL_MAX_CHARS = 2000000
export const TOOL_SAVED_MAX_CHARS = 200000

// The hub's own font families (src/styles/fonts.css), as a tool names them
export const TOOL_FAMILIES = ['Archivo', 'IBM Plex Sans', 'Inter', 'Inter Tight', 'Manrope', 'Anton']

const escapeRe = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// The hub families a tool's HTML names: in a font-family list or a custom property ('Archivo',
// "IBM Plex Sans", Manrope,) or in a Google Fonts link (family=IBM+Plex+Sans:wght@400). "Inter"
// isn't found inside "Inter Tight", nor in a word like "interest".
export function familiesIn(html) {
  const text = String(html || '')
  return TOOL_FAMILIES.filter(name => {
    const n = escapeRe(name)
    const plus = escapeRe(name).replace(/ /g, '[ +]')
    return new RegExp(`['"]\\s*${n}\\s*['"]`, 'i').test(text)
      || new RegExp(`family=${plus}(?=[:&"'|]|$)`, 'i').test(text)
      || new RegExp(`(?:font-family\\s*:|-font\\s*:|,)\\s*${n}\\s*(?=[,;}"'])`, 'i').test(text)
  })
}

// @font-face rules for the families, each weight from the data it was given:
// fonts = { Archivo: [{ weight: 500, data: 'data:font/woff2;base64,...' }], ... }
export function fontFaceCss(fonts) {
  const rules = []
  for (const [family, faces] of Object.entries(fonts || {})) {
    for (const face of faces || []) {
      if (!face || !/^data:font\/woff2;base64,[A-Za-z0-9+/=]+$/.test(face.data || '')) continue
      rules.push(`@font-face{font-family:'${family}';font-style:normal;font-display:swap;font-weight:${Number(face.weight) || 400};src:url(${face.data}) format('woff2')}`)
    }
  }
  return rules.join('\n')
}

// Google Fonts out: its <link>s (stylesheet and preconnect) and any @import of it
export function withoutGoogleFonts(html) {
  return String(html || '')
    .replace(/<link\b[^>]*\bhref\s*=\s*["']?https?:\/\/fonts\.(?:googleapis|gstatic)\.com[^>]*>/gi, '')
    .replace(/@import\s+(?:url\()?\s*["']?https?:\/\/fonts\.(?:googleapis|gstatic)\.com[^;]*;/gi, '')
}

// What a script element may hold: no closing tag, and nothing that reads as HTML
const scriptSafe = value => JSON.stringify(value).replace(/</g, '\\u003c').replace(/>/g, '\\u003e')
  .replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029')

// A new channel for 1 copy of a tool: 128 random bits as hex, never kept anywhere
export function toolChannel() {
  const bytes = new Uint8Array(16)
  globalThis.crypto.getRandomValues(bytes)
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

// The stand-ins and the height report, run before anything of the tool's own. Every message carries
// the channel, so the hub knows it comes from this copy of the tool.
function bootScript(hubOrigin, saved, channel) {
  return `(function(){
var hub=${scriptSafe(hubOrigin)},saved=${scriptSafe(saved)},channel=${scriptSafe(channel)};
function tell(m){m.channel=channel;try{parent.postMessage(m,hub)}catch(e){}}
function store(items,keep){
var m={},own=Object.prototype.hasOwnProperty;
for(var k in items){if(own.call(items,k)&&typeof items[k]==='string')m[k]=items[k]}
function changed(){if(keep)tell({hubTool:'storage',items:m})}
var api={
getItem:function(k){k=String(k);return own.call(m,k)?m[k]:null},
setItem:function(k,v){m[String(k)]=String(v);changed()},
removeItem:function(k){delete m[String(k)];changed()},
clear:function(){m={};changed()},
key:function(i){var ks=Object.keys(m);return i>=0&&i<ks.length?ks[i]:null}
};
Object.defineProperty(api,'length',{get:function(){return Object.keys(m).length}});
return api}
try{Object.defineProperty(window,'localStorage',{value:store(saved||{},true),configurable:true,enumerable:true})}catch(e){}
try{Object.defineProperty(window,'sessionStorage',{value:store({},false),configurable:true,enumerable:true})}catch(e){}
var last=0;
function size(){var d=document.documentElement,b=document.body;if(!d)return;
var h=Math.ceil(Math.max(d.getBoundingClientRect().height,b?b.scrollHeight:0,b?b.offsetHeight:0));
if(h>0&&Math.abs(h-last)>1){last=h;tell({hubTool:'height',height:h})}}
try{new ResizeObserver(size).observe(document.documentElement)}catch(e){}
document.addEventListener('DOMContentLoaded',size);
window.addEventListener('load',size);
document.addEventListener('input',size,true);
document.addEventListener('click',function(){setTimeout(size,50)},true);
[250,700,1400].forEach(function(t){setTimeout(size,t)});
})();`
}

// The tool, ready to run: Google Fonts out, the policy, the hub's fonts and the boot script in
// first, at the top of its <head>.
//   hubOrigin: the hub page's origin, the only place the tool's messages go
//   saved: what this member's copy of the tool kept last time ({ key: value }), or {}
//   fontsCss: fontFaceCss's rules for the families it names
//   channel: toolChannel()'s value for this copy, which every message from it carries
export function prepareToolHtml(html, { hubOrigin, saved = {}, fontsCss = '', channel = '' } = {}) {
  const body = withoutGoogleFonts(html)
  const head = [
    '<meta charset="utf-8">',
    `<meta http-equiv="Content-Security-Policy" content="${TOOL_META_CSP}">`,
    `<script>${bootScript(hubOrigin || '', saved || {}, String(channel || ''))}</script>`,
    fontsCss ? `<style>${fontsCss}</style>` : '',
  ].join('')
  const headTag = /<head\b[^>]*>/i.exec(body)
  if (headTag) return body.slice(0, headTag.index + headTag[0].length) + head + body.slice(headTag.index + headTag[0].length)
  const htmlTag = /<html\b[^>]*>/i.exec(body)
  if (htmlTag) return `${body.slice(0, htmlTag.index + htmlTag[0].length)}<head>${head}</head>${body.slice(htmlTag.index + htmlTag[0].length)}`
  const doctype = /^\s*<!doctype[^>]*>/i.exec(body)
  if (doctype) return `${doctype[0]}<head>${head}</head>${body.slice(doctype[0].length)}`
  return `<!doctype html><head>${head}</head>${body}`
}

// A message from the tool's frame: { kind: 'ready' } | { kind: 'height', height } |
// { kind: 'storage', items } | null for anything else (a tool's own messages, or a malformed one).
// 'ready' comes from the runner page, which has no channel; the tool's height and working count only
// with this copy's channel, so a page the frame has navigated to can't send them.
export function readToolMessage(data, channel) {
  if (!data || typeof data !== 'object') return null
  if (data.hubTool === 'ready') return { kind: 'ready' }
  if (typeof channel !== 'string' || channel === '' || data.channel !== channel) return null
  if (data.hubTool === 'height' && Number.isFinite(data.height) && data.height > 0) {
    return { kind: 'height', height: Math.min(Math.ceil(data.height), 40000) }
  }
  if (data.hubTool === 'storage' && data.items && typeof data.items === 'object' && !Array.isArray(data.items)) {
    const items = {}
    for (const [k, v] of Object.entries(data.items)) if (typeof v === 'string') items[k] = v
    return { kind: 'storage', items }
  }
  return null
}

// Where a member's working in a tool is kept, in the hub's own storage: per person and per item
export const SAVED_PREFIX = 'hub-tool:'
export const savedKey = (personId, itemId) => `${SAVED_PREFIX}${personId}:${itemId}`

// Takes the tools' working out of a Storage (localStorage): 1 person's when personId is given,
// otherwise every tool's working kept in this browser. Answers how many keys went.
export function clearSavedWorking(storage, personId) {
  if (!storage) return 0
  const prefix = personId ? `${SAVED_PREFIX}${personId}:` : SAVED_PREFIX
  const going = []
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i)
    if (typeof k === 'string' && k.startsWith(prefix)) going.push(k)
  }
  for (const k of going) storage.removeItem(k)
  return going.length
}

// The runner page itself (/tools/run.html). It waits to be given a tool by the page that framed
// it, from the hub's own address, then writes the tool in its place. Opened on its own, it does
// nothing.
export function runnerPage() {
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${TOOL_META_CSP}">
<meta name="robots" content="noindex, nofollow">
<meta name="referrer" content="no-referrer">
<title>Tool</title>
<style>html,body{margin:0;background:transparent}</style>
</head>
<body>
<script>
(function () {
  if (window.parent === window) return;
  var done = false;
  window.addEventListener('message', function (e) {
    if (done || e.source !== window.parent || e.origin !== location.origin) return;
    var d = e.data;
    if (!d || d.hubTool !== 'run' || typeof d.html !== 'string') return;
    done = true;
    document.open();
    document.write(d.html);
    document.close();
  });
  window.parent.postMessage({ hubTool: 'ready' }, location.origin);
})();
</script>
</body>
</html>
`
}
